import { useState, useMemo } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper,
} from '@mui/material'
import { GridView, Search } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'
import { formatNumber } from '../../utils/format'

function weekDate(weeksAgo) {
  const d = new Date(2026, 2, 10)
  d.setDate(d.getDate() - weeksAgo * 7)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const WEEKS = Array.from({ length: 12 }, (_, i) => weekDate(11 - i))

const DEMO_DATA = [
  { category: 'Pallets Total', values: [4200,4350,4180,4420,4550,4380,4500,4620,4480,4700,4580,4650] },
  { category: 'Purchased Inv', values: [1200,1250,1180,1300,1280,1220,1350,1400,1320,1380,1350,1400] },
  { category: 'Finished', values: [2200,2280,2200,2320,2400,2360,2350,2420,2380,2500,2430,2450] },
  { category: 'Brite', values: [800,820,800,800,870,800,800,800,780,820,800,800] },
]

function TrendChart({ data, width = 400, height = 100 }) {
  const values = data[0].values
  const max = Math.max(...values) * 1.1
  const min = Math.min(...values) * 0.9
  const range = max - min || 1

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width
    const y = height - ((v - min) / range) * height
    return `${x},${y}`
  }).join(' ')

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Pallets Total — 12 Week Trend</Typography>
      <svg width={width} height={height + 20} viewBox={`0 0 ${width} ${height + 20}`} style={{ maxWidth: '100%' }}>
        <polyline
          points={points}
          fill="none"
          stroke="#1A9E2A"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {values.map((v, i) => {
          const x = (i / (values.length - 1)) * width
          const y = height - ((v - min) / range) * height
          return <circle key={i} cx={x} cy={y} r="3" fill="#1A9E2A" />
        })}
        <line x1="0" y1={height + 2} x2={width} y2={height + 2} stroke="#C5CFC5" strokeWidth="1" />
      </svg>
    </Box>
  )
}

export default function PalletCount() {
  const [search, setSearch] = useState('')

  const rows = useMemo(
    () => DEMO_DATA.filter((r) => r.category.toLowerCase().includes(search.toLowerCase())),
    [search],
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <GridView color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Pallet Count</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="Warehouse Pallet Tracking" size="small" variant="outlined" />
        <Typography variant="overline" color="text.secondary">
          As of Mar 10, 2026 · v12 · {DEMO_DATA.length} categories
        </Typography>
      </Box>

      <TextField
        size="small"
        placeholder="Search categories…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 280 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <TrendChart data={DEMO_DATA} />

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>Category</TableCell>
              {WEEKS.map((w) => (
                <TableCell key={w} align="right" sx={{ fontWeight: 700, minWidth: 70 }}>{w}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.category} hover sx={row.category === 'Pallets Total' ? { bgcolor: 'action.hover' } : {}}>
                <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: row.category === 'Pallets Total' ? 'action.hover' : 'background.paper', zIndex: 1 }}>
                  {row.category}
                </TableCell>
                {row.values.map((v, i) => (
                  <TableCell key={i} align="right">{formatNumber(v)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
